import { createApi } from "@reduxjs/toolkit/query/react";
import customFetchBase from "./customFetchBase";

import { ProductSettingSaveInput } from "../../pages/admin/product.page";
import { PriceSettingSaveInput } from "../../pages/admin/price.page";
import {
  IGenericResponse,
  IPlanDetail,
  IPlanDetailsUser,
  IProduct,
  IProductHeadings,
  ICampDetailsUser
} from "./types";

export const productApi = createApi({
  reducerPath: "productApi",
  baseQuery: customFetchBase,
  tagTypes: ["Product"],
  endpoints: (builder) => ({
    getProduct: builder.query<IProduct, IProductHeadings>({
      query({ product_name, product_module }) {
        return {
          url: `products?product_name=${product_name}&product_module=${product_module}`,
        };
      },
      transformResponse: (results: { data: IProduct }) => results.data,
      providesTags: [{ type: "Product", id: "LIST" }],
    }),
     getAppsumoProduct: builder.query<IProduct, IProductHeadings>({
      query({ product_name, product_module }) {
        return {
          url: `products/appsumoproducts?product_name=${product_name}&product_module=${product_module}`,
        };
      },
      transformResponse: (results: { data: IProduct }) => results.data,
      providesTags: [{ type: "Product", id: "LIST" }],
    }),
    addProduct: builder.mutation<IGenericResponse, ProductSettingSaveInput>({
      query(data) {
        return {
          url: "products",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: [{ type: "Product", id: "LIST" }],
    }),
    updateProduct: builder.mutation<IGenericResponse, ProductSettingSaveInput>({
      query(data) {
        return {
          url: "products",
          method: "PATCH",
          body: data,
        };
      },
      invalidatesTags: [{ type: "Product", id: "LIST" }],
    }),
    searchProduct: builder.query<IProductHeadings[], string>({
      query(searchKey: string) {
        return {
          url: `products/searchbyid?product_id=${searchKey}`,
        };
      },
      transformResponse: (results: { data: IProductHeadings[] }) =>
        results.data,
    }),
    updatePrice: builder.mutation<IGenericResponse, PriceSettingSaveInput>({
      query(data) {
        return {
          url: "products/update_price",
          method: "PATCH",
          body: data,
        };
      },
    }),
    getProductsNames: builder.query<string[], string | void>({
      query(product_id) {
        return {
          url: "products/names?product_id="+product_id,
          method: "GET",
        };
      },
      transformResponse: (results: { data: string[] }) => results.data,
    }),
    getModules: builder.query<string[], string>({
      query(product_name: string) {
        return {
          url: `products/modules?product_name=${product_name}`,
          method: "GET",
        };
      },
      transformResponse: (results: { data: { product_module: string }[] }) =>
        results.data.map((item) => item.product_module),
    }),
    getPrices: builder.query<
      IPlanDetail[] | null,
      { product_name: string; product_module: string }
    >({
      query({ product_name, product_module }) {
        return {
          url: `products/prices?product_name=${product_name}&product_module=${product_module}`,
          method: "GET",
        };
      },
      transformResponse: (results: {
        data: { plan_details: IPlanDetail[] } | null;
      }) => (results.data ? results.data.plan_details : null),
    }),
    getAddOn: builder.query<any, any>({
      query() {
        return {
          url: "products/get-add-on",
          method: "POST",
        };
      },
    }),
    getPricesDetails: builder.query<
    IPlanDetail[] | null,
    { price_id: string }
  >({
    query({ price_id }) {
      return {
        url: `products/getPirceDetails?price_id=${price_id}`,
        method: "GET",
      };
    },
    transformResponse: (results: {
      data: { plan_details: IPlanDetail[] } | null;
    }) => (results.data ? results.data.plan_details : null),
  }),


  planDetails: builder.mutation<IPlanDetailsUser, { email: string, campaignid: string }>({
    query({email, campaignid}) {
      return {
        url: `products/get-plan-details?email=${email}&campaignid=${campaignid}`,
        method: "GET",
      };
    },
  }),

  campaignDetails: builder.mutation<ICampDetailsUser, { campaignid: string }>({
    query({campaignid}) {
      return {
        url: `products/get_campaign_details?campaignid=${campaignid}`,
        method: "GET",
      };
    },
  }),


  }),
});

export const {
  useUpdateProductMutation,
  useLazyGetProductQuery,
  useLazyGetAppsumoProductQuery,
  useGetProductQuery,
  useLazySearchProductQuery,
  useUpdatePriceMutation,
  useAddProductMutation,
  useGetProductsNamesQuery,
  useLazyGetModulesQuery,
  useLazyGetPricesQuery,
  useGetAddOnQuery,
  useGetPricesDetailsQuery,
  usePlanDetailsMutation,
  useCampaignDetailsMutation
} = productApi;
